import { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Check, X, Pencil, Save, History, Sparkles } from "lucide-react";

const STATUS = {
  vide: { label: "Vide", cls: "bg-muted text-muted-foreground" },
  a_valider: { label: "À valider", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  valide: { label: "Validé", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
};

const Suggestion = ({ s, onAccept, onReject, busy }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(s.text);
  return (
    <div className="rounded-lg border border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-2" data-testid={`suggestion-${s.id}`}>
      <div className="flex items-center gap-2 text-xs text-amber-800 dark:text-amber-300"><Sparkles className="w-3.5 h-3.5" /> Proposition issue de <span className="font-mono" data-testid={`suggestion-source-${s.id}`}>{s.source}</span></div>
      {editing ? <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid={`suggestion-edit-${s.id}`} /> : <p className="text-sm whitespace-pre-wrap">{s.text}</p>}
      <div className="flex flex-wrap gap-2 justify-end">
        <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={onReject} disabled={busy} data-testid={`suggestion-reject-${s.id}`}><X className="w-3.5 h-3.5 mr-1" /> Rejeter</Button>
        {editing ? (
          <Button size="sm" className="h-7" onClick={() => onAccept(text)} disabled={busy || !text.trim()} data-testid={`suggestion-accept-edited-${s.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Valider la version modifiée</Button>
        ) : (
          <>
            <Button size="sm" variant="outline" className="h-7" onClick={() => setEditing(true)} disabled={busy} data-testid={`suggestion-edit-btn-${s.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Modifier</Button>
            <Button size="sm" className="h-7" onClick={() => onAccept()} disabled={busy} data-testid={`suggestion-accept-${s.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Valider</Button>
          </>
        )}
      </div>
    </div>
  );
};

const AnamnesisField = ({ fieldKey, label, data, onSave, onAccept, onReject }) => {
  const f = data || { content: "", status: "vide", suggestions: [], entries: [] };
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(f.content || "");
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const status = STATUS[f.status] || STATUS.vide;
  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  return (
    <div className="py-4 border-b last:border-b-0" data-testid={`anamnesis-field-${fieldKey}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="text-sm font-medium">{label}</p>
        <div className="flex items-center gap-1.5 shrink-0">
          {f.suggestions?.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500 text-white" data-testid={`pending-count-${fieldKey}`}>{f.suggestions.length}</span>}
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${status.cls}`} data-testid={`status-${fieldKey}`}>{status.label}</span>
          {f.entries?.length > 0 && <button type="button" onClick={() => setShowHistory(!showHistory)} className="text-muted-foreground hover:text-foreground" title="Historique des sources" data-testid={`history-btn-${fieldKey}`}><History className="w-3.5 h-3.5" /></button>}
          {!editing && <button type="button" onClick={() => { setDraft(f.content || ""); setEditing(true); }} className="text-muted-foreground hover:text-primary" title="Modifier" data-testid={`edit-btn-${fieldKey}`}><Pencil className="w-3.5 h-3.5" /></button>}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} autoFocus data-testid={`field-textarea-${fieldKey}`} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditing(false)} disabled={busy}>Annuler</Button>
            <Button size="sm" className="h-7" onClick={() => run(async () => { await onSave(draft, true); setEditing(false); })} disabled={busy} data-testid={`field-save-${fieldKey}`}><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer et valider</Button>
          </div>
        </div>
      ) : f.content ? (
        <p className="text-sm whitespace-pre-wrap text-foreground/90" data-testid={`field-content-${fieldKey}`}>{f.content}</p>
      ) : (
        <p className="text-sm italic text-muted-foreground" data-testid={`field-empty-${fieldKey}`}>Non renseigné — cliquez sur le crayon pour compléter.</p>
      )}

      {showHistory && f.entries?.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground" data-testid={`history-${fieldKey}`}>
          {f.entries.map((e, i) => <li key={i} className="font-mono">• {e.source} — {e.action}</li>)}
        </ul>
      )}

      {f.suggestions?.length > 0 && (
        <div className="mt-3 space-y-2">
          {f.suggestions.map((s) => (
            <Suggestion key={s.id} s={s} busy={busy} onAccept={(text) => run(() => onAccept(s.id, text))} onReject={() => run(() => onReject(s.id))} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AnamnesisField;
